import * as DialogPrimitive from '@radix-ui/react-dialog'
import { cn } from 'lib/utils'
import { XIcon } from 'lucide-react'
import * as React from 'react'

function Dialog({ ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />
}

function DialogTrigger({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogClose({ ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

interface DialogOverlayProps extends React.ComponentProps<typeof DialogPrimitive.Overlay> {
  variant?: 'default' | 'dark'
}

function DialogOverlay({ className, variant = 'default', ...props }: DialogOverlayProps) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        'fixed inset-0 z-50 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0',
        // Default: legacy translucent black overlay.
        variant === 'default' && 'bg-black/50',
        // T3: dense black overlay with backdrop-blur for the dark canvas
        // moment. Stays calm — never tinted, never animated beyond the fade.
        variant === 'dark' && 'bg-black/70 backdrop-blur-sm',
        className
      )}
      {...props}
    />
  )
}

interface DialogContentProps extends React.ComponentProps<typeof DialogPrimitive.Content> {
  size?: 'sm' | 'md' | 'lg'
  withCloseButton?: boolean
  /**
   * `default` — legacy white shadcn dialog. `primary` — legacy Taka-purple form
   * dialog. `dark` — T3 dual-theme dialog (flips with `data-theme`). Use `dark`
   * for any new dialog in a migrated surface.
   */
  variant?: 'default' | 'primary' | 'dark'
}

function DialogContent({
  className,
  children,
  size = 'sm',
  withCloseButton = true,
  variant = 'default',
  ...props
}: DialogContentProps) {
  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay variant={variant === 'dark' ? 'dark' : 'default'} />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          'fixed top-[50%] left-[50%] z-50 grid max-h-[90vh] w-full translate-x-[-50%] translate-y-[-50%] overflow-y-hidden duration-200 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
          // Default variant styling
          variant === 'default' && 'gap-4 rounded-lg border bg-white p-6 shadow-lg',
          // Primary variant styling for Taka forms
          variant === 'primary' &&
            'rounded-[18px] border-0 bg-white p-10 pb-8 shadow-[0px_15px_32.7px_0px_rgba(21,21,116,0.14)]',
          variant === 'dark' &&
            'gap-4 rounded-[12px] border border-line bg-bg-2 p-6 text-paper shadow-[var(--shadow-card)]',
          size === 'sm' && 'max-w-[calc(100%-2rem)] sm:max-w-lg',
          size === 'md' && 'max-w-[calc(100%-2rem)] sm:max-w-2xl',
          size === 'lg' && 'max-w-[calc(100%-2rem)] sm:max-w-6xl',
          className
        )}
        {...props}
      >
        {children}
        {withCloseButton && (
          <DialogPrimitive.Close
            className={cn(
              'absolute top-4 right-4 cursor-pointer rounded-[6px] opacity-70 transition-opacity hover:opacity-100 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*=size-])]:size-4',
              variant === 'dark'
                ? 'text-paper-dim hover:text-paper focus-visible:ring-acid focus-visible:ring-offset-bg-2'
                : 'ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground'
            )}
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
}

interface DialogHeaderProps extends React.ComponentProps<'div'> {
  variant?: 'default' | 'primary'
}

function DialogHeader({ className, variant = 'default', ...props }: DialogHeaderProps) {
  return (
    <div
      data-slot="dialog-header"
      className={cn(
        // Default variant
        variant === 'default' && 'flex flex-col gap-2 text-center sm:text-left',
        // Primary variant for Taka forms
        variant === 'primary' && 'flex w-full flex-col items-center space-y-1.5',
        className
      )}
      {...props}
    />
  )
}

function DialogFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
      {...props}
    />
  )
}

interface DialogTitleProps extends React.ComponentProps<typeof DialogPrimitive.Title> {
  variant?: 'default' | 'primary' | 'dark'
}

function DialogTitle({ className, variant = 'default', ...props }: DialogTitleProps) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn(
        // Default variant
        variant === 'default' && 'text-center text-lg leading-none font-semibold',
        // Primary variant for Taka forms
        variant === 'primary' && 'text-center text-2xl leading-[1.4] font-semibold text-[#101828]',
        // T3: paper text, h1-tight scale (smaller for modal context).
        variant === 'dark' &&
          'text-[20px] leading-[1.2] font-semibold tracking-[-0.01em] text-paper',
        className
      )}
      {...props}
    />
  )
}

interface DialogDescriptionProps extends React.ComponentProps<typeof DialogPrimitive.Description> {
  variant?: 'default' | 'primary' | 'dark'
}

function DialogDescription({ className, variant = 'default', ...props }: DialogDescriptionProps) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(
        // Default variant
        variant === 'default' && 'text-center text-base leading-[1.4] font-normal',
        // Primary variant for Taka forms
        variant === 'primary' && 'text-center text-base leading-[1.4] font-normal text-[#667085]',
        // T3: paper-dim, soft lede sizing.
        variant === 'dark' && 'text-[14px] leading-[1.55] text-paper-dim',
        className
      )}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
